import { Drawer } from '@mui/material';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { updateEngagementPlansListAction } from '@redux/tasks/tasksActions';
import { createEngagementPlanThunk, scheduleEngagementPlanThunk } from '@redux/tasks/tasksThunks';
import { EngagementPlan, EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { BackIconButton } from 'components/Button/BackButton/BackIconButton';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { CustomizeSection } from 'components/EngagementPlanUpdateDrawer/components/CustomizeSection/CustomizeSection';
import { PreviewSection } from 'components/EngagementPlanUpdateDrawer/components/PreviewSection/PreviewSection';
import { WarningCircleIcon } from 'components/Icons';
import { StartPlanModal } from 'containers/PatientDashboard/components/StartPlanModal';
import { countEngagementPlanTasks } from 'utils/taskUtils';
import { TaskEditArea } from './components/TaskEditArea/TaskEditArea';
import style from './EngagementPlanUpdateDrawer.scss';

type EngagementPlanUpdateDrawerProps = {
  plan: EngagementPlan;
  open: boolean;
  onClose: () => void;
  patientId: string;
  onRefreshData?: () => void;
};

type Props = ConnectedProps<typeof connectRedux> & EngagementPlanUpdateDrawerProps;

const EngagementPlanUpdateDrawer: React.FC<Props> = ({
  plan,
  open,
  onClose,
  scheduleEngagementPlan,
  createEngagementPlan,
  updateEngagementPlansList,
  patientId,
  onRefreshData
}) => {
  const [updatingPlanData, setUpdatingPlanData] = useState<EngagementPlan>({} as EngagementPlan);
  const [showStartPlanModal, setShowStartPlanModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<EngagementPlanTask | null>(null);

  const { showAlertInfo, showAlertWarning } = useAlert();

  const onCloseDrawer = () => {
    onClose();
    setSelectedTask(null);
    setShowConfirmModal(false);
    setShowStartPlanModal(false);
  };

  const onConfirmStartPlan = async (startDate: Date) => {
    if (!patientId) return;

    try {
      const { data: newPlan } = await createEngagementPlan({
        ...updatingPlanData,
        title: capitalize(updatingPlanData.title.trim()),
        description: capitalize(updatingPlanData.description?.trim())
      });
      updateEngagementPlansList({ plan: { ...newPlan }, add: true });
      await scheduleEngagementPlan(newPlan.id, patientId, startDate);
      showAlertInfo({
        message: 'Engagement plan was successfully started!'
      });
      onRefreshData?.();
      onCloseDrawer();
    } catch (error) {
      showAlertWarning({
        message: 'Failed to start engagement plan. Please try again.'
      });
      throw error;
    }
  };

  const onSaveUpdatedTask = (updatedTask: EngagementPlanTask) => {
    const trimmedUpdatedTask = {
      ...updatedTask,
      deliverableMessage: updatedTask.deliverableMessage ? capitalize(updatedTask.deliverableMessage.trim()) : ''
    };
    const updatedTasks = updatingPlanData.tasks?.map((task) => (task.taskName === updatedTask.taskName ? trimmedUpdatedTask : task)) || [];
    setUpdatingPlanData({
      ...updatingPlanData,
      tasks: updatedTasks,
      taskCount: countEngagementPlanTasks({ tasks: updatedTasks, durationDays: updatingPlanData.durationDays } as EngagementPlan).total
    });
    setSelectedTask(null);
    showAlertInfo({
      title: 'Done!',
      message: 'Your tasks have been updated.'
    });
  };

  useEffect(() => {
    !!plan && setUpdatingPlanData(plan);
  }, [plan]);

  const onCloseWithConfirmation = () => {
    setShowConfirmModal(true);
  };

  if (!plan) {
    return null;
  }

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onCloseWithConfirmation} variant="temporary">
        <div className={style.drawerContainer}>
          <PreviewSection plan={updatingPlanData} containerStyle={style.leftSection} />

          <div className={style.rightSection}>
            <div className={style.drawerHeader}>
              <CloseIconButton containerClassName={style.closeButton} onClick={onCloseWithConfirmation} />
            </div>
            <CustomizeSection plan={updatingPlanData} setUpdatingPlanData={setUpdatingPlanData} setSelectedTask={setSelectedTask} />
            <div className={style.drawerFooter}>
              <Button variant="contained" onClick={() => setShowStartPlanModal(true)} className={style.continueButton}>
                Start plan
              </Button>
            </div>
          </div>
          {selectedTask && (
            <div className={classNames(style.rightSection, style.overlaySection)}>
              <div className={classNames(style.drawerHeader, style.fullDrawerHeader)}>
                <BackIconButton onClick={() => setSelectedTask(null)} />
                <CloseIconButton onClick={onCloseWithConfirmation} />
              </div>
              <TaskEditArea task={selectedTask} duration={updatingPlanData.durationDays} onSaveUpdatedTask={onSaveUpdatedTask} />
            </div>
          )}
        </div>
        <StartPlanModal open={showStartPlanModal} onClose={() => setShowStartPlanModal(false)} engagementPlan={updatingPlanData} onConfirm={onConfirmStartPlan} />
        <ConfirmationDialogV2
          open={showConfirmModal}
          icon={WarningCircleIcon}
          title="You have unsaved changes"
          description="If you close the plan options now, the changes will be lost. Do you wish to exit?"
          proceedEvent={onCloseDrawer}
          proceedEventText={'Yes, exit'}
          closeEvent={() => setShowConfirmModal(false)}
          closeEventText={'Go back'}
        />
      </Drawer>
    </>
  );
};

const connectRedux = connect(() => ({}), {
  scheduleEngagementPlan: scheduleEngagementPlanThunk,
  createEngagementPlan: createEngagementPlanThunk,
  updateEngagementPlansList: updateEngagementPlansListAction
});

export default compose(connectRedux)(EngagementPlanUpdateDrawer);
